import { useEffect, useState } from 'react';
import { Truck, ChevronDown, CheckCircle2, XCircle } from 'lucide-react';
import { apiFetch } from '../lib/api';
import { formatPrice } from '../lib/utils';
import { useCMS } from '../hooks/useCMS';
import { useCartStore } from '../store/cartStore';

// 跨境運費試算 widget v2（shipping-zones 2.0「定性配對」）
// mode="product"：單商品試算（產品頁）——不可寄時「此商品未能寄往 {區}」＋原因（定性）
// mode="cart"：整車試算（購物車側欄）——逐項列被擋品與原因
// 目的地選單徽章（計算非儲存）：白名單含 food→「✅ 可寄食品」；不含→「⚠️ 食品類除外」
// 文案全部 CMS 可編（key 前綴 shipping.widget.*；未種子時用 fallback）
// v2：zones 回全部區（含停用）——停用區＝定性可判、運費「價目即將公佈」。

interface ZoneOption {
  id: number;
  name: string;
  code: string;
  enabled: boolean;
  freeOverCents: number | null;
  foodAllowed: boolean | null;
  rates: { id: number; label: string; etaDays: string | null }[];
}

interface QuoteRate {
  id: number;
  label: string;
  etaDays: string | null;
  costCents: number;
  freeShipping: boolean;
}

interface QuoteResult {
  zone: { id: number; name: string; code: string; enabled: boolean; freeOverCents: number | null; foodAllowed: boolean | null };
  shippable: boolean;
  blockedItems: { productId: number; productName: string; natureName: string | null; reason: string | null }[];
  items: { productId: number; productName: string; qty: number; shippable: boolean; natureCode: string; natureName: string; reason: string | null }[];
  rates: QuoteRate[];
}

interface ShippingQuoteWidgetProps {
  mode: 'product' | 'cart';
  productId?: number;
  priceCents?: number;
}

export default function ShippingQuoteWidget({ mode, productId, priceCents }: ShippingQuoteWidgetProps) {
  const { getValue } = useCMS();
  const cartItems = useCartStore((s) => s.items);
  const [open, setOpen] = useState(false);
  const [zones, setZones] = useState<ZoneOption[] | null>(null); // null＝載入中
  const [selectedCode, setSelectedCode] = useState('');
  const [quote, setQuote] = useState<QuoteResult | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || zones !== null) return;
    apiFetch('/api/public/shipping/zones')
      .then((res) => res.json())
      .then((json) => setZones(Array.isArray(json) ? json : json?.data || []))
      .catch(() => setZones([]));
  }, [open, zones]);

  useEffect(() => {
    if (!open || !selectedCode) { setQuote(null); return; }
    const items =
      mode === 'product'
        ? [{ productId: Number(productId || 0), qty: 1, priceCents: Number(priceCents || 0) }]
        : cartItems.map((i) => ({ productId: i.productId, qty: i.quantity, priceCents: i.unitPrice }));
    if (!items.length) { setQuote(null); return; }
    apiFetch('/api/public/shipping/quote', {
      method: 'POST',
      body: JSON.stringify({ destinationCode: selectedCode, items }),
    })
      .then((res) => res.json())
      .then((json) => {
        if (Array.isArray(json) && json.length) setQuote(json[0]);
        else { setQuote(null); setError(json?.error || getValue('shipping.widget.quote_error', '暫時無法取得運費，請稍後再試')); }
      })
      .catch(() => setError(getValue('shipping.widget.quote_error', '暫時無法取得運費，請稍後再試')));
  }, [open, selectedCode, mode, productId, priceCents, cartItems, getValue]);

  const title = getValue('shipping.widget.title', '📦 運費試算');
  const destLabel = getValue('shipping.widget.destination_label', '選擇目的地');
  const disabledNote = getValue('shipping.widget.disabled_note', '運費試算即將開放——落單前歡迎透過 WhatsApp 查詢運費與時效。');
  const etaLabel = getValue('shipping.widget.eta_label', '時效');
  const freeLabel = getValue('shipping.widget.free_label', '免運');
  const freeOverHint = getValue('shipping.widget.free_over_hint', '滿 {amount} 免運');
  const cartNotShippable = getValue('shipping.widget.cart_not_shippable', '購物車內有商品未能寄往該目的地');
  const shippableLabel = getValue('shipping.widget.shippable', '可寄往該目的地');
  const cartShippableLabel = getValue('shipping.widget.cart_shippable', '購物車商品全數可寄');
  const noRates = getValue('shipping.widget.no_rates', '此目的地暫未提供運費線種');
  const ratesPending = getValue('shipping.widget.rates_pending', '該目的地運費價目即將公佈——落單前歡迎透過 WhatsApp 查詢。');
  const foodOkBadge = getValue('shipping.natures.food_allowed', '✅ 可寄食品');
  const foodBlockedBadge = getValue('shipping.natures.food_blocked', '⚠️ 食品類除外');
  const notShippableZone = (zoneName: string) =>
    getValue('shipping.widget.not_shippable_zone', '此商品未能寄往 {zone}').replace('{zone}', zoneName);

  const selectedZone = (zones || []).find((z) => z.code === selectedCode) || null;
  const zoneList = zones || [];

  const badgeText = (z: { foodAllowed: boolean | null }) =>
    z.foodAllowed === true ? foodOkBadge : z.foodAllowed === false ? foodBlockedBadge : '';

  return (
    <div className="border border-stone-200 rounded-2xl bg-stone-50/60">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 text-left"
        aria-expanded={open}
      >
        <span className="font-bold text-sm flex items-center gap-2 text-stone-800">
          <Truck size={18} className="text-x1493-primary" />
          {title}
        </span>
        <ChevronDown size={18} className={`text-stone-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-3">
          {zones === null ? (
            <p className="text-sm text-stone-500">載入中...</p>
          ) : zoneList.length === 0 ? (
            <p className="text-sm text-stone-500">{disabledNote}</p>
          ) : (
            <>
              <label className="block">
                <span className="text-xs font-bold text-stone-500 block mb-1">{destLabel}</span>
                <select
                  value={selectedCode}
                  onChange={(e) => { setSelectedCode(e.target.value); setError(''); }}
                  className="w-full border border-stone-300 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-x1493-primary/30"
                >
                  <option value="">—</option>
                  {zoneList.map((z) => (
                    <option key={z.id} value={z.code}>
                      {z.name}{badgeText(z) ? `　${badgeText(z)}` : ''}
                    </option>
                  ))}
                </select>
              </label>

              {selectedZone && badgeText(selectedZone) && (
                <p className={`text-xs font-bold ${selectedZone.foodAllowed ? 'text-green-700' : 'text-amber-700'}`}>
                  {badgeText(selectedZone)}
                </p>
              )}

              {mode === 'cart' && cartItems.length === 0 && (
                <p className="text-sm text-stone-500">購物車是空的</p>
              )}

              {error && <p className="text-sm text-red-600">{error}</p>}

              {quote && (
                <div className="space-y-2">
                  {!quote.shippable && (
                    <div className="flex items-start gap-2 text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-3 py-2">
                      <XCircle size={16} className="mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-bold">
                          {mode === 'product' ? notShippableZone(quote.zone.name) : cartNotShippable}
                        </p>
                        {quote.blockedItems.length > 0 && (
                          <ul className="mt-1 space-y-0.5">
                            {quote.blockedItems.map((b) => (
                              <li key={b.productId} className="text-xs text-red-500">
                                · {b.productName}（{b.reason || `${b.natureName || '品類'}不可寄`}）
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                  )}

                  {quote.rates.length === 0 ? (
                    <p className="text-sm text-stone-500">
                      {quote.zone.enabled ? noRates : ratesPending}
                    </p>
                  ) : (
                    quote.rates.map((r) => (
                      <div key={r.id} className="flex items-center justify-between bg-white border border-stone-200 rounded-xl px-3 py-2 text-sm">
                        <div>
                          <p className="font-bold text-stone-800">{r.label}</p>
                          {r.etaDays && (
                            <p className="text-xs text-stone-500">{etaLabel}：{r.etaDays}</p>
                          )}
                        </div>
                        <div className="text-right">
                          <p className={`font-bold ${r.freeShipping ? 'text-green-600' : 'text-x1493-primary'}`}>
                            {r.freeShipping ? freeLabel : formatPrice(r.costCents)}
                          </p>
                          {!r.freeShipping && quote.zone.freeOverCents != null && (
                            <p className="text-[11px] text-stone-400">
                              {freeOverHint.replace('{amount}', formatPrice(quote.zone.freeOverCents))}
                            </p>
                          )}
                        </div>
                      </div>
                    ))
                  )}

                  {quote.shippable && quote.rates.length > 0 && (
                    <p className="flex items-center gap-1.5 text-xs text-green-700">
                      <CheckCircle2 size={14} />
                      {mode === 'product' ? shippableLabel : cartShippableLabel}
                    </p>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
